import { t } from 'i18next';
import upperCase from 'lodash/upperCase';
import isEmpty from 'lodash/isEmpty';
import { MenuProps } from 'antd/es/menu';
import { EngineConfigStatus } from '@/store/Engine/constants';
import isNil from 'lodash/isNil';
import { EngineStore } from '@/store/Engine/EngineStore';
import { StationParameter } from '@/store/Engine/common';
import { NotificationInstance } from 'antd/es/notification/interface';
import { NavigateFunction } from 'react-router-dom';

interface DataItem {
  key: string;
  label: string;
  name: string;
  input: string | number;
  suffix?: string;
}

export const getBasicData = (
  stationParameters: StationParameter[],
  tunit: (unit: string, format: string) => string,
  unit: string,
): DataItem[] => {
  return [
    {
      key: '1',
      label: t('targetPressure'),
      name: 'stationTargetPressure',
      input: stationParameters[0]?.stationTargetPressure,
      suffix: tunit('Bar', upperCase(unit)),
    },
    {
      key: '2',
      label: t('offsetFromTargetPressure'),
      name: 'normalPressureOffsetFromTarget',
      suffix: tunit('Bar', upperCase(unit)),
      input: stationParameters[0]?.normalPressureOffsetFromTarget,
    },
    {
      key: '3',
      label: t('pressureForReloadingCompressor'),
      name: 'reloadPressure',
      input: stationParameters[0]?.reloadPressure,
      suffix: tunit('Bar', upperCase(unit)),
    },
    {
      key: '4',
      label: t('pressureForStartingCompressor'),
      name: 'startPressure',
      input: stationParameters[0]?.startPressure,
      suffix: tunit('Bar', upperCase(unit)),
    },
    {
      key: '5',
      label: t('pressureForEmergencyStart'),
      name: 'emergencyStartPressure',
      input: stationParameters[0]?.emergencyStartPressure,
      suffix: tunit('Bar', upperCase(unit)),
    },
    {
      key: '6',
      label: t('minLoadedCompressors'),
      name: 'minAllowedActiveCompressors',
      input: stationParameters[0]?.minAllowedActiveCompressors,
    },
  ];
};

export const getAdvancedData = (
  stationParameters: StationParameter[],
  tunit: (unit: string, format: string) => string,
  unit: string,
): DataItem[] => {
  return [
    {
      key: '1',
      label: t('pressureFactor'),
      name: 'unloadingTestPressureFactor',
      input: stationParameters[0]?.unloadingTestPressureFactor,
      suffix: tunit('Bar', upperCase(unit)),
    },
    {
      key: '2',
      label: t('minFrequency'),
      name: 'minAllowedUnloadTestingAttemptRateMinutes',
      input: stationParameters[0]?.minAllowedUnloadTestingAttemptRateMinutes,
      suffix: t('Min'),
    },
    {
      key: '3',
      label: t('minPressureSafetyMargin'),
      name: 'unloadCommandMinTargetSafetyPercentage',
      input: stationParameters[0]?.unloadCommandMinTargetSafetyPercentage,
      suffix: t('%'),
    },
    {
      key: '4',
      label: t('minCompressorStartInterval'),
      name: 'minAllowedStartTimeIntervalMinutes',
      input: stationParameters[0]?.minAllowedStartTimeIntervalMinutes,
      suffix: t('Min'),
    },
    {
      key: '5',
      label: t('minCompressorRestartInterval'),
      name: 'minAllowedRestartTimeIntervalMinutes',
      input: stationParameters[0]?.minAllowedRestartTimeIntervalMinutes,
      suffix: t('Min'),
    },
    {
      key: '6',
      label: t('flowPeakBuffer'),
      name: 'unloadTestingFlowPeakBuffer',
      input: stationParameters[0]?.unloadTestingFlowPeakBuffer,
      suffix: t('CFM'),
    },
    {
      key: '7',
      label: t('minIntervalHighFlowTrigger'),
      name: 'minIntervalFromHighFlowTrigger',
      input: stationParameters[0]?.minIntervalFromHighFlowTrigger,
      suffix: t('Min'),
    },
    {
      key: '8',
      label: t('setpointChangeInterval'),
      name: 'unloadTestingCommandFrequencySeconds',
      suffix: t('sec'),
      input: stationParameters[0]?.unloadTestingCommandFrequencySeconds,
    },
    {
      key: '9',
      label: t('kpiIntervalRetrieval'),
      name: 'kpiTimeIntervalMinutes',
      suffix: t('Min'),
      input: stationParameters[0]?.kpiTimeIntervalMinutes,
    },
    {
      key: '10',
      label: t('loadBalancingPressureFactor'),
      name: 'loadBalancingPressureFactor',
      suffix: tunit('Bar', upperCase(unit)),
      input: stationParameters[0]?.loadBalancingPressureFactor,
    },
    {
      key: '11',
      label: t('emergencyStartFrequency'),
      name: 'emergencyStartPriorityRotationDelayMinutes',
      input: stationParameters[0]?.emergencyStartPriorityRotationDelayMinutes,
      suffix: t('Min'),
    },
    {
      key: '12',
      label: t('bovMin'),
      name: 'bovAvgUpperBoundThresholdPercentage',
      input: stationParameters[0]?.bovAvgUpperBoundThresholdPercentage,
      suffix: t('%'),
    },
    {
      key: '13',
      label: t('bovMax'),
      name: 'compressorBovUpperBoundThresholdPercentage',
      input: stationParameters[0]?.compressorBovUpperBoundThresholdPercentage,
      suffix: t('%'),
    },
    {
      key: '14',
      label: t('fullLoadPressureBuffer'),
      name: 'maxLoadBuffer',
      input: stationParameters[0]?.maxLoadBuffer,
      suffix: t('%'),
    },
    {
      key: '15',
      label: t('unloadTestingFlowBufferPercentage'),
      name: 'unloadTestingFlowBufferPercentage',
      input: stationParameters[0]?.unloadTestingFlowBufferPercentage,
      suffix: t('%'),
    },
  ];
};

export const versionTabs = {
  published: 'published',
  draft: 'draft',
  recommendation: 'recommendation',
  engine: 'engine', // wizard
  versionHistory: 'versionHistory',
};

export type NotificationType = 'success' | 'info' | 'warning' | 'error';

export const getMenuItems = (
  setOpenPublishModal: (value: boolean) => void,
  setOpenRevertModal: (value: boolean) => void,
  onDiscardChanges: () => void,
  engineStore: EngineStore,
  disablePublish: boolean,
): MenuProps['items'] => {
  return [
    {
      key: '1',
      label: t('publishChanges'),
      onClick: () => setOpenPublishModal(true),
      disabled: disablePublish,
    },
    {
      key: '2',
      label: t('revertToLastPublished'),
      onClick: () => setOpenRevertModal(true),
      disabled:
        engineStore.isReadOnly ||
        isEmpty(engineStore.publishedEngineConfig) ||
        engineStore.areDraftAndPublishedEqual ||
        engineStore.hasPIDChanges ||
        engineStore.hasKpiChanges,
    },
    {
      key: '3',
      label: t('Discard Changes'),
      onClick: onDiscardChanges,
      disabled: engineStore.isReadOnly || !engineStore.hasChanges(),
    },
  ];
};

export const handleActiveTabChange = (activeTab: string, engineStore: EngineStore, tvalue) => {
  if (activeTab === EngineConfigStatus.Published) {
    engineStore.setEngineConfig(engineStore.publishedEngineConfig, tvalue);
  } else if (activeTab === EngineConfigStatus.Draft) {
    engineStore.setEngineConfig(engineStore.draftEngineConfig, tvalue);
  } else if (activeTab === EngineConfigStatus.Recommendation) {
    engineStore.setEngineConfig(engineStore.automaticEngineConfig, tvalue);
  }
};

export const fetchEngineSupport = async (engineStore, stationStore, navigate: (path: string) => void) => {
  await engineStore.getEngineSupport();
  if (engineStore.isReady && engineStore.engineSupport) {
    if (engineStore.isCentrifugalOnlyStation) {
      navigate(`/home/station/${stationStore.currentStation?.id}/centrifugal`);
    } else {
      navigate(`/home/station/${stationStore.currentStation?.id}/engine`);
    }
  }
};

export const handleTabChange = async (
  tab: string,
  engineStore: EngineStore,
  tvalue,
  setActiveTab: (tab: string) => void,
  hasViewPermission: boolean,
) => {
  if (!hasViewPermission) return;

  switch (tab) {
    case 'published':
      engineStore.setEngineConfig(engineStore.publishedEngineConfig, tvalue);
      break;

    case 'draft':
      if (isNil(engineStore.draftEngineConfig) && !isEmpty(engineStore.publishedEngineConfig)) {
        await engineStore.createNewDraft(tvalue);
      }
      break;

    case 'recommendation':
      if (!window.localStorage.getItem('hasVisitedRecommendedTab')) {
        window.localStorage.setItem('hasVisitedRecommendedTab', 'true');
        await engineStore.viewRecommendation();
      }
      break;

    case 'versionHistory':
      engineStore.getVersionHistory();
      break;

    default:
      console.warn(`Unhandled tab: ${tab}`);
      break;
  }

  setActiveTab(tab);
};

export const getTabTitle = (engineConfigObj: { configurationStatus?: string }, engineStore: EngineStore): string => {
  if (engineConfigObj?.configurationStatus === EngineConfigStatus.Published) {
    return versionTabs.published;
  } else if (engineConfigObj?.configurationStatus === EngineConfigStatus.Draft) {
    return isEmpty(engineStore.publishedEngineConfig) ? 'newDraft' : versionTabs.draft;
  } else if (engineConfigObj?.configurationStatus === EngineConfigStatus.Recommendation) {
    return 'recommended';
  } else {
    return 'createNewDraft';
  }
};

export const openNotification = (
  api: NotificationInstance,
  type: NotificationType,
  message: string,
  description: string,
  duration = 5,
) => {
  api[type]({
    message,
    description,
    placement: 'bottomLeft',
    duration,
  });
};

export const handleDiscardChanges = (
  isMobile: boolean,
  setShowDrawer: (show: boolean) => void,
  engineStore: EngineStore,
) => {
  if (isMobile) {
    setShowDrawer(false);
  }
  engineStore.cancelChanges();
};

export const handlePreSaveChanges = async (
  engineStore: EngineStore,
  tvalue,
  setIsLoading: (loading: boolean) => void,
  setIsSaveDisabled: (disabled: boolean) => void,
  setDisablePublish: (disabled: boolean) => void,
  api: NotificationInstance,
  validateStationParameters: () => boolean,
  validateCompressorParameters: () => boolean,
) => {
  setIsLoading(true);

  engineStore.clearValidationMessages();
  engineStore.validateAll(tvalue);
  if (engineStore.validationMessages?.length) {
    setIsLoading(false);
    if (!engineStore.validationMessages.some(message => message.message === 'errorSavingDraftdesc')) {
      openNotification(api, t('warning'), t('errorSavingDraft'), t(engineStore.validationMessages[0].message), 3);
      return;
    }

    openNotification(api, t('warning'), t('errorSavingDraft'), t('errorSavingDraftdesc'), 3);
    return;
  }

  if (
    !engineStore.stationConfiguration?.stationParameter ||
    !engineStore.compressorsConfiguration?.compressorParameters ||
    !engineStore.sensorsConfiguration?.flowSensorsPriority ||
    !engineStore.sensorsConfiguration?.pressureSensorsPriority
  ) {
    setIsSaveDisabled(true);
    setIsLoading(false);
    openNotification(api, t('warning'), t('errorSavingDraft'), t('errorSavingDraftdesc'), 3);
    return;
  }

  if (!validateStationParameters() || !validateCompressorParameters()) {
    return;
  }

  try {
    await engineStore.saveEngineConfig(tvalue);

    openNotification(api, t('success'), t('draftSaved'), t('draftSavedDesc'), 3);
    setIsLoading(false);
    setIsSaveDisabled(false);
    setDisablePublish(false);
  } catch (error) {
    setIsLoading(false);
    setIsSaveDisabled(false);
    setDisablePublish(true);
    openNotification(api, t('error'), t('errorSavingDraft'), t('errorSavingChanges'), 3);
  }
};

export const handleRevertToLastPublished = async (
  engineStore: EngineStore,
  tvalue,
  setIsLoadingRevert: (loading: boolean) => void,
  navigate: NavigateFunction,
  api: NotificationInstance,
) => {
  try {
    setIsLoadingRevert(true);
    await engineStore.revertToPublished(tvalue);
    setIsLoadingRevert(false);
    navigate(0);
  } catch (error) {
    setIsLoadingRevert(false);
    openNotification(api, t('error'), t('errorRevertLastPublished'), t('errorRevertLastPublishedDesc'), 3);
  }
};

export const handlePublish = async (
  validateCompressorParameters: () => boolean,
  validateStationParameters: () => boolean,
  setOpenPublishModal: (open: boolean) => void,
  setIsLoadingPublish: (loading: boolean) => void,
  isMobile: boolean,
  setShowDrawer: (show: boolean) => void,
  engineStore: EngineStore,
  tvalue,
  api: NotificationInstance,
  setActiveTab: (tab) => void,
  versionTabs,
) => {
  try {
    if (!validateCompressorParameters() || !validateStationParameters()) {
      setOpenPublishModal(false);
      return;
    }

    setIsLoadingPublish(true);
    if (isMobile) {
      setShowDrawer(false);
    }

    await engineStore.publishDraft(tvalue);
    setIsLoadingPublish(false);
    openNotification(api, t('success'), t('successPublish'), t('successPublishDesc'), 3);
    setOpenPublishModal(false);
    setTimeout(() => {
      setActiveTab(versionTabs.published);
      engineStore.getEngineConfig(tvalue);
    }, 2000);
  } catch (error) {
    openNotification(api, t('error'), t('errorDraftPublish'), t('errorDraftPublishDesc'), 3);
  }
};

export const validateCompressorParameters = (
  compressorParams,
  setIsSaveDisabled: (disabled: boolean) => void,
  setIsLoading: (loading: boolean) => void,
  api: NotificationInstance,
  setActiveCentrifugalTab: (tab) => void,
  EngineTab,
) => {
  for (const key in compressorParams) {
    const param = compressorParams[key];
    const requiredFields = ['minTarget', 'maxTarget', 'stopDelayMinutes', 'priority'];

    const hasMissingFields = requiredFields.some(field => param[field] == null || param[field] === '');

    if (hasMissingFields) {
      setIsSaveDisabled(true);
      setIsLoading(false);
      openNotification(api, t('warning'), t('incompleteCompressorParameters'), t('compressorParametersMissing'), 5);
      setActiveCentrifugalTab(EngineTab.CompressorParameters);
      return false;
    }
  }
  return true;
};

export const validateStationParameters = (
  stationParams,
  setIsSaveDisabled: (disabled: boolean) => void,
  setIsLoading: (loading: boolean) => void,
  api: NotificationInstance,
  setActiveCentrifugalTab: (tab) => void,
  EngineTab,
) => {
  for (const key in stationParams) {
    if (stationParams[key] == null || stationParams[key] === '') {
      setIsSaveDisabled(true);
      setIsLoading(false);
      openNotification(api, t('warning'), t('incompleteStationParameters'), t('stationParametersMissing'), 5);
      setActiveCentrifugalTab(EngineTab.StationConfiguration);
      return false;
    }
  }
  return true;
};

export const emptyTableData = [
  { key: 'skeleton-1', label: '', input: '' },
  { key: 'skeleton-2', label: '', input: '' },
  { key: 'skeleton-3', label: '', input: '' },
  { key: 'skeleton-4', label: '', input: '' },
  { key: 'skeleton-5', label: '', input: '' },
  { key: 'skeleton-6', label: '', input: '' },
];
